import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getEmployeeSelfReviewCyclesAPI, 
  getEmployeePeerReviewCyclesAPI, 
  getEmployeePeerReviewEmployeesAPI,
  getEmployeeAppraisalResponsesAPI
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface CycleWithEmployees extends AvailableCycle {
  completedEmployees: Employee[];
  pendingEmployees: Employee[];
}

const EmployeeAppraisals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selfReviewCycles, setSelfReviewCycles] = useState<AvailableCycle[]>([]);
  const [peerReviewCyclesWithEmployees, setPeerReviewCyclesWithEmployees] = useState<CycleWithEmployees[]>([]);
  const [completedSelfResponses, setCompletedSelfResponses] = useState<AppraisalResponse[]>([]);
  const [completedPeerResponses, setCompletedPeerResponses] = useState<AppraisalResponse[]>([]);

  useEffect(() => {
    fetchSelfReviewCycles();
    fetchPeerReviewCycles();
    fetchCompletedResponses();
  }, []);

  const fetchSelfReviewCycles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEmployeeSelfReviewCyclesAPI();
      setSelfReviewCycles(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load self review cycles.");
      toast.error("Failed to load self review cycles");
    } finally {
      setLoading(false);
    }
  };

  const fetchPeerReviewCycles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEmployeePeerReviewCyclesAPI();
      const cycles = response.data;
      
      const cyclesWithStatus = await Promise.all(
        cycles.map(async (cycle) => {
          try {
            const statusResponse = await getEmployeePeerReviewEmployeesAPI(cycle.id);
            const completed = statusResponse.data.completed || [];
            const pending = statusResponse.data.pending || [];
            
            return {
              ...cycle,
              completedEmployees: completed,
              pendingEmployees: pending
            };
          } catch (err) {
            console.error(`Failed to fetch peer employees for cycle ${cycle.id}:`, err);
            return {
              ...cycle,
              completedEmployees: [],
              pendingEmployees: []
            };
          }
        })
      );
      
      setPeerReviewCyclesWithEmployees(cyclesWithStatus);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load peer review cycles.");
      toast.error("Failed to load peer review cycles");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedResponses = async () => {
    try {
      const [selfResponse, peerResponse] = await Promise.all([
        getEmployeeAppraisalResponsesAPI('SELF', 'SUBMITTED'),
        getEmployeeAppraisalResponsesAPI('PEER', 'SUBMITTED')
      ]);
      setCompletedSelfResponses(selfResponse.data);
      setCompletedPeerResponses(peerResponse.data);
    } catch (err: any) {
      console.error("Failed to load completed responses:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  if (loading && selfReviewCycles.length === 0 && peerReviewCyclesWithEmployees.length === 0) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      <TabsContainer defaultTab={0}>
        <Tab isActive={false} title="Self Reviews">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Self Reviews
            </h2>

            {selfReviewCycles.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No self review cycles available at this time.
              </div>
            ) : (
              <div className="space-y-4">
                {/* Replace the selfReviewCycles.map section with this: */}
                {selfReviewCycles.map((cycle) => {
                  const completedResponse = completedSelfResponses.find(r => r.cycle.id === cycle.id);
                  const isCompleted = !!completedResponse;

                  return (
                    <div
                      key={cycle.id}
                      className="rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700"
                      onClick={() => {
                        if (isCompleted) {
                          navigate(`/employee-appraisals/response/${completedResponse.id}`);
                        } else if (!isDeadlinePassed(cycle.deadline)) {
                          navigate(`/employee-appraisals/self/${cycle.id}`);
                        }
                      }}
                    >
                      {/* HEADER: Added this missing block to show Title and Description */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                            {cycle.name}
                          </h3>
                          {cycle.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {cycle.description}
                            </p>
                          )}
                        </div>
                        {isCompleted ? (
                          <span className="px-2 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-navy-600 rounded-full flex-shrink-0">
                            Submitted
                          </span>
                        ) : isDeadlinePassed(cycle.deadline) ? (
                          <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 rounded">
                            Deadline Passed
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          {isCompleted && completedResponse?.submitted_at ? (
                            <>
                              <i className="fas fa-calendar-check text-gray-500 dark:text-gray-400"></i>
                              <span>
                                {formatDate(completedResponse.submitted_at)}
                              </span>
                            </>
                          ) : (
                            <>
                              <span>Deadline:</span>
                              <span className="font-medium text-navy-700 dark:text-white">{formatDate(cycle.deadline)}</span>
                            </>
                          )}
                        </div>
                        
                        <Button
                          varient={isCompleted ? "secondary" : "primary"}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevents double navigation
                            if (isCompleted) {
                              navigate(`/employee-appraisals/response/${completedResponse.id}`);
                            } else {
                              navigate(`/employee-appraisals/self/${cycle.id}`);
                            }
                          }}
                          disabled={!isCompleted && isDeadlinePassed(cycle.deadline)}
                        >
                          {isCompleted ? "View Response" : "Fill Review"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="Peer Reviews">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Peer Reviews
            </h2>

            {peerReviewCyclesWithEmployees.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No peer review cycles available at this time.
              </div>
            ) : (
              <div className="space-y-4">
                {peerReviewCyclesWithEmployees.map((cycle) => (
                  <div
                    key={cycle.id}
                    className="border border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                          {cycle.name}
                        </h3>
                        {cycle.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {cycle.description}
                          </p>
                        )}
                      </div>
                      {isDeadlinePassed(cycle.deadline) && (
                        <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 dark:bg-red-900/20 rounded">
                          Deadline Passed
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Form Opens: </span>
                        <span className="text-navy-700 dark:text-white font-medium">
                          {formatDate(cycle.form_opens)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Deadline: </span>
                        <span className="text-navy-700 dark:text-white font-medium">
                          {formatDate(cycle.deadline)}
                        </span>
                      </div>
                    </div>

                    {cycle.completedEmployees && cycle.completedEmployees.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                          Completed ({cycle.completedEmployees.length}):
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cycle.completedEmployees.map((employee) => {
                            const response = completedPeerResponses.find(
                              r => r.cycle.id === cycle.id && r.employee.id === employee.id
                            );
                            return (
                              <div
                                key={employee.id}
                                onClick={() => response && navigate(`/employee-appraisals/response/${response.id}`)}
                                className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer ${
                                  isDeadlinePassed(cycle.deadline) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  {employee.photo ? (
                                    <img
                                      src={employee.photo}
                                      alt={employee.full_name || "Employee"}
                                      className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                      <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                        {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-navy-700 dark:text-white truncate">
                                      {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                                    </p>
                                    {employee.email && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {employee.email}
                                      </p>
                                    )}
                                  </div>
                                  <i className="fas fa-check-circle text-gray-500 dark:text-gray-400 text-lg"></i>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {cycle.pendingEmployees && cycle.pendingEmployees.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                          Pending ({cycle.pendingEmployees.length}):
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {cycle.pendingEmployees.map((employee) => (
                            <div
                              key={employee.id}
                              onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/employee-appraisals/peer/${cycle.id}/employee/${employee.id}`)}
                              className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer ${
                                isDeadlinePassed(cycle.deadline) ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {employee.photo ? (
                                  <img
                                    src={employee.photo}
                                    alt={employee.full_name || "Employee"}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                  />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                    <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                      {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                    </span>
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-navy-700 dark:text-white truncate">
                                    {employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown'}
                                  </p>
                                  {employee.email && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {employee.email}
                                    </p>
                                  )}
                                  {employee.designation && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                      {employee.designation}
                                    </p>
                                  )}
                                </div>
                                <i className="fas fa-clock text-gray-500 dark:text-gray-400 text-lg"></i>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="Completed Appraisals">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Completed Appraisals
            </h2>

            {completedSelfResponses.length === 0 && completedPeerResponses.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No completed appraisals yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...completedSelfResponses, ...completedPeerResponses].map((response) => (
                  <div
                    key={response.id}
                    className="bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/employee-appraisals/response/${response.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2">
                          {response.cycle.name}
                        </h3>
                        <div className="flex items-center gap-3 mb-3">
                          {response.reviewer_type === 'PEER' && response.employee?.photo ? (
                            <img
                              src={response.employee.photo}
                              alt={response.employee?.full_name || "Employee"}
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                            />
                          ) : response.reviewer_type === 'PEER' ? (
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                              <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm">
                                {(response.employee?.first_name?.[0] || '') + (response.employee?.last_name?.[0] || '')}
                              </span>
                            </div>
                          ) : null}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-navy-700 dark:text-white truncate">
                              {response.reviewer_type === 'PEER' 
                                ? (response.employee?.full_name || `${response.employee?.first_name || ''} ${response.employee?.last_name || ''}`.trim() || 'Unknown')
                                : 'Self Review'
                              }
                            </p>
                            {response.reviewer_type === 'PEER' && response.employee?.email && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {response.employee.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-navy-600 rounded-full flex-shrink-0">
                        {response.reviewer_type === 'SELF' ? 'Self' : 'Peer'}
                      </span>
                    </div>

                    <div className="pt-3 border-t border-gray-200 dark:border-navy-700">
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <i className="fas fa-calendar-check text-gray-500 dark:text-gray-400"></i>
                        <span>
                          {response.submitted_at ? formatDate(response.submitted_at) : (response.created_at ? formatDate(response.created_at) : 'N/A')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>
      </TabsContainer>
    </div>
  );
};

export default EmployeeAppraisals;
